// The web app's state: account, workspace, loaded time ranges and sessions. Screens retain the ranges they show;
// a change anywhere (live stream) reloads what is retained. Edits apply locally at once, then go to the server.
import { ApiError, type Api } from '../api/client.ts';
import { clearGuest, guestChanges, guestHasData, loadGuest, switchGuest } from '../api/guest.ts';
import type { Change, Me, RawEvent, TimeEntry, TimeEntryVersion, WorkspacesResponse } from '../api/types.ts';
import type { Formats } from '../lib/format.ts';
import type { Lang, Translate } from '../lib/i18n.ts';
import {
  emptyRange, emptyWorkspace, onlyMember, rangeFrom, sessionRangeFrom, workspaceFrom, type RangeData, type Workspace,
} from '../model/data.ts';
import type { Actor } from '../model/mutations.ts';
import type { ReportGrouping, ReportPeriod } from '../model/report.ts';

const DRAFT_KEY = 'tally.timerDraft';

function loadDraft(workspaceId: string, memberId: string): { description: string; projectId: string | null } {
  try {
    const raw = globalThis.localStorage?.getItem(`${DRAFT_KEY}.${workspaceId}.${memberId}`);
    const parsed = raw ? (JSON.parse(raw) as { description?: unknown; projectId?: unknown }) : {};
    return { description: typeof parsed.description === 'string' ? parsed.description : '', projectId: typeof parsed.projectId === 'string' ? parsed.projectId : null };
  } catch {
    return { description: '', projectId: null };
  }
}

export type Phase = 'loading' | 'signedOut' | 'needsWorkspace' | 'ready' | 'failed';
export type LiveState = 'connecting' | 'connected' | 'offline';

export interface Loadable<T> {
  data: T | undefined;
  loading: boolean;
  error: string | null;
}

interface RangeEntry extends Loadable<RangeData> {
  /** Your own time in a team (the screens); `data` is everything the server sent (team reports). */
  mine?: RangeData;
  key: string;
  start: number;
  end: number;
  usage: boolean;
  retained: number;
  stale: boolean;
  lastUsed: number;
}

interface SessionEntry extends Loadable<RangeData | null> {
  id: string;
  retained: number;
  stale: boolean;
  events: RawEvent[] | undefined;
}

export const rangeKey = (start: number, end: number, usage: boolean) => `${start}-${end}-${usage ? 1 : 0}`;

export type EntriesMode = 'day' | 'week' | 'list';
export type SessionsFilter = 'live' | 'today' | 'week' | 'month';

/** What the screens remember while you move around (like the Mac's AppModel). */
export interface UiState {
  entriesMode: EntriesMode;
  /** An instant inside the day or week the entries screen shows; `null` = today. */
  entriesAnchor: number | null;
  entrySelection: string[];
  reportPeriod: ReportPeriod;
  reportCustom: { start: number; end: number } | null;
  reportGrouping: ReportGrouping;
  reportTable: boolean;
  /** Owners in a team: the team's time in reports (else your own). */
  reportTeam: boolean;
  sessionsFilter: SessionsFilter;
  sessionSearch: string;
  /** The timer's text and project before it starts (kept in this browser). */
  draftDescription: string;
  draftProjectId: string | null;
}

export type SheetState =
  | { kind: 'entry'; entryId: string | null; day: number | null }
  | { kind: 'project'; projectId: string | null; then?: (projectId: string) => void }
  | { kind: 'export'; projectId: string | null }
  | { kind: 'invoice'; range: { start: number; end: number } }
  | { kind: 'reportPrint'; range: { start: number; end: number } }
  | { kind: 'spanEdit'; sessionId: string; range: { start: number; end: number }; mode: 'reassign' | 'split' | 'discard' }
  | { kind: 'addDevice' }
  | { kind: 'search' };

export interface StoreOptions {
  api: Api;
  clock: () => number;
  timeZone: string;
}

export class Store {
  readonly api: Api;
  readonly clock: () => number;
  readonly timeZone: string;
  lang: Lang = 'de';
  t: Translate = (key) => key;
  fmt!: Formats;

  phase: Phase = 'loading';
  me: Me | null = null;
  workspaces: WorkspacesResponse | null = null;
  workspaceSwitching = false;
  ws: Workspace = emptyWorkspace();
  seq = 0;
  live: LiveState = 'connecting';
  /** The last failed change or load, shown as a banner until dismissed. */
  problem: string | null = null;
  /** Good news worth a banner (the guest's data arrived in the account), until dismissed. */
  notice: string | null = null;
  /** Pushes on their way to the server. */
  pending = 0;
  lastSaved: number | null = null;
  ui: UiState;
  sheet: SheetState | null = null;
  /** Start and Stop rest for a moment after each change, so repeated clicks can't pile up entries. */
  timerSettling = false;
  /** How long Start and Stop rest after a change (tests set zero). */
  timerSettleMs = 1500;
  private settleTimeout: ReturnType<typeof setTimeout> | null = null;

  private ranges = new Map<string, RangeEntry>();
  private sessions = new Map<string, SessionEntry>();
  private listeners = new Set<() => void>();
  private version = 0;
  private stopLive: (() => void) | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private generation = 0;
  private meRequest = 0;
  private workspacesRequest = 0;

  constructor(options: StoreOptions) {
    this.api = options.api;
    this.clock = options.clock;
    this.timeZone = options.timeZone;
    this.ui = {
      entriesMode: 'day', entriesAnchor: null, entrySelection: [], reportPeriod: 'thisMonth', reportCustom: null, reportGrouping: 'project',
      reportTable: false, reportTeam: true, sessionsFilter: 'today', sessionSearch: '', draftDescription: '', draftProjectId: null,
    };
  }

  setUi(patch: Partial<UiState>): void {
    this.ui = { ...this.ui, ...patch };
    if ('draftDescription' in patch || 'draftProjectId' in patch) {
      try {
        if (this.me?.workspace) globalThis.localStorage?.setItem(`${DRAFT_KEY}.${this.me.workspace.id}.${this.me.workspace.memberId}`, JSON.stringify({ description: this.ui.draftDescription, projectId: this.ui.draftProjectId }));
      } catch { /* private mode */ }
    }
    this.changed();
  }

  openSheet(sheet: SheetState): void {
    this.sheet = sheet;
    this.changed();
  }

  closeSheet(): void {
    this.sheet = null;
    this.changed();
  }

  // MARK: Subscription (useSyncExternalStore)

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getVersion = (): number => this.version;

  /** Call after a start or stop: until the rest is over, the timer controls ignore further clicks. */
  settleTimer(): void {
    if (this.timerSettleMs <= 0) return;
    this.timerSettling = true;
    if (this.settleTimeout) clearTimeout(this.settleTimeout);
    this.settleTimeout = setTimeout(() => {
      this.settleTimeout = null;
      this.timerSettling = false;
      this.changed();
    }, this.timerSettleMs);
    this.changed();
  }

  private changed(): void {
    this.version += 1;
    for (const l of this.listeners) l();
  }

  setLanguage(lang: Lang, t: Translate, fmt: Formats): void {
    this.lang = lang;
    this.t = t;
    this.fmt = fmt;
    this.changed();
  }

  // MARK: Account

  async start(): Promise<void> {
    const generation = ++this.generation;
    this.phase = 'loading';
    this.changed();
    try {
      const me = await this.api.me();
      if (generation !== this.generation) return;
      this.me = me;
      this.api.setWorkspace(me?.workspace?.id ?? null);
      if (me && !this.api.guest) {
        const workspaces = await this.api.workspaces();
        if (generation !== this.generation) return;
        this.workspaces = workspaces;
        const active = workspaces.workspaces.find((w) => w.id === me.workspace?.id);
        if (me.workspace) {
          if (!active || workspaces.activeWorkspaceId !== me.workspace.id || active.memberId !== me.workspace.memberId
            || active.role !== (me.workspace.role ?? 'member')) throw new ApiError(409, 'workspace_changed');
          this.me = { ...me, workspace: active };
        }
      }
      const draft = this.me?.workspace ? loadDraft(this.me.workspace.id, this.me.workspace.memberId) : { description: '', projectId: null };
      this.ui = { ...this.ui, draftDescription: draft.description, draftProjectId: draft.projectId };
      if (!this.me) {
        this.phase = 'signedOut';
      } else if (!this.me.workspace) {
        this.phase = 'needsWorkspace';
      } else {
        if (!this.api.guest && !this.api.demo) await this.adoptGuest();
        if (generation !== this.generation) return;
        await this.loadBase();
        if (generation !== this.generation) return;
        this.phase = 'ready';
        this.connectLive();
      }
    } catch (error) {
      if (generation !== this.generation) return;
      this.phase = 'failed';
      this.problem = this.describe(error);
    }
    this.changed();
  }

  actor(): Actor {
    const ws = this.me?.workspace;
    return { workspaceId: ws?.id ?? '', memberId: ws?.memberId ?? '', memberName: ws?.memberName || this.me?.user.name || '', timeZone: this.timeZone };
  }

  /** Pro, as the server said (spec/pro.md). Only a display: the server refuses Pro changes without it anyway. */
  get pro(): boolean {
    return this.me?.pro?.active ?? false;
  }

  get memberName(): string {
    return this.me?.workspace?.memberName || this.me?.user.name || this.me?.user.email || '';
  }

  /**
   * After signing in: what this browser created as a guest goes into the account. A workspace claimed with the guest's
   * ids takes everything; an existing one keeps its settings, and the entries become yours there. If the server
   * refuses (a member may not create projects), the data stays in this browser.
   */
  private async adoptGuest(): Promise<void> {
    const generation = this.generation;
    const state = loadGuest();
    const ws = this.me?.workspace;
    if (!ws?.personal || !state) return;
    try {
      if (!guestHasData(state)) { await clearGuest(state); return; }
      const results = await this.api.push(guestChanges(state, ws.memberId, ws.id === state.workspaceId));
      if (generation !== this.generation) return;
      if (results.some((r) => r !== 'ok' && r !== 'stale')) throw new ApiError(422, 'invalid');
      const cleared = await clearGuest(state);
      if (generation !== this.generation) return;
      if (cleared) this.notice = this.t('Deine Daten aus dem Gastmodus sind jetzt in deinem Konto.');
    } catch {
      if (generation !== this.generation) return;
      this.problem = this.t('Deine Daten aus dem Gastmodus ließen sich nicht übernehmen. Sie bleiben in diesem Browser: Melde dich ab und fahre als Gast fort, um sie zu sehen.');
    }
  }

  /** Before creating an account with the guest's data: the new workspace takes the guest's ids. */
  guestIds(): { workspaceId: string; memberId: string } | null {
    const state = loadGuest();
    return state && guestHasData(state) ? { workspaceId: state.workspaceId, memberId: state.memberId } : null;
  }

  dismissNotice(): void {
    this.notice = null;
    this.changed();
  }

  async signOut(): Promise<void> {
    const generation = this.generation;
    // A guest leaves for sign-in; the page loads again with the server behind it.
    if (this.api.guest) { await this.api.logout(); switchGuest(false); return; }
    try { await this.api.logout(); }
    catch (error) {
      if (generation !== this.generation) return;
      if (!(error instanceof ApiError && error.status === 401)) throw error;
    }
    if (generation !== this.generation) return;
    this.reset();
    this.phase = 'signedOut';
    this.changed();
  }

  /** After sign-in or claiming a workspace. */
  async reload(): Promise<void> {
    this.reset();
    await this.start();
  }

  async refreshWorkspaces(): Promise<void> {
    const generation = this.generation;
    const request = ++this.workspacesRequest;
    try {
      const workspaces = await this.api.workspaces();
      if (generation !== this.generation || request !== this.workspacesRequest) return;
      this.workspaces = workspaces;
      this.changed();
    } catch (error) {
      if (generation === this.generation && request === this.workspacesRequest) throw error;
    }
  }

  /** Never change the destination of a write in flight or leave a hidden running timer behind. */
  private async canSwitchWorkspace(): Promise<void> {
    if (this.pending > 0) throw new ApiError(409, 'changes_pending');
    if (!this.me?.workspace) return;
    const range = await this.api.range(this.clock() - 1, this.clock() + 1, false);
    if (range.entries.some((e) => e.memberId === this.me?.workspace?.memberId && e.endAt === null && e.deletedAt === null)) throw new ApiError(409, 'timer_running');
  }

  async selectWorkspace(id: string): Promise<void> {
    if (this.workspaceSwitching || id === this.me?.workspace?.id) return;
    if (this.pending > 0) throw new ApiError(409, 'changes_pending');
    const original = this.me;
    const originalId = original?.workspace?.id ?? null;
    const phase = this.phase;
    const generation = ++this.generation;
    this.workspaceSwitching = true;
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    this.stopLive?.();
    this.stopLive = null;
    // Keep the old data until the target is ready, but invalidate reads already in flight.
    for (const entry of [...this.ranges.values(), ...this.sessions.values()]) {
      if (entry.loading) { entry.loading = false; entry.stale = true; }
    }
    this.phase = 'loading';
    this.changed();
    let selectionAttempted = false;
    try {
      await this.canSwitchWorkspace();
      if (generation !== this.generation) return;
      selectionAttempted = true;
      await this.api.selectWorkspace(id);
      if (generation !== this.generation) return;
      this.api.setWorkspace(id);
      const [me, workspaces, base] = await Promise.all([this.api.me(), this.api.workspaces(), this.api.base()]);
      if (generation !== this.generation) return;
      if (!me || me.user.id !== original?.user.id) throw new ApiError(401, 'unauthorized');
      const target = workspaces.workspaces.find((workspace) => workspace.id === id);
      if (!target || workspaces.activeWorkspaceId !== id || me.workspace?.id !== id
        || me.workspace.memberId !== target.memberId || me.workspace.role !== target.role
        || (base.you && (base.you.memberId !== target.memberId || base.you.role !== target.role))) {
        throw new ApiError(409, 'workspace_changed');
      }
      const workspace = workspaceFrom({ ...base, you: base.you ?? { memberId: target.memberId, role: target.role } });
      this.reset();
      this.api.setWorkspace(id);
      this.me = { ...me, workspace: target };
      this.workspaces = workspaces;
      this.ws = workspace;
      this.seq = base.seq;
      const draft = loadDraft(id, target.memberId);
      this.ui = { ...this.ui, draftDescription: draft.description, draftProjectId: draft.projectId };
      this.phase = 'ready';
      this.connectLive();
    } catch (error) {
      if (generation !== this.generation) throw error;
      let accessChanged = false;
      let rollbackError: unknown = null;
      if (selectionAttempted && originalId && !(error instanceof ApiError && error.status === 401)) {
        try {
          const restored = await this.api.selectWorkspace(originalId);
          accessChanged = restored.memberId !== original?.workspace?.memberId || restored.role !== original?.workspace?.role;
        } catch (e) {
          rollbackError = e;
          // The original header still prevents a write to an unconfirmed server selection.
          accessChanged = e instanceof ApiError && e.status === 403;
        }
      }
      if (generation !== this.generation) throw error;
      this.api.setWorkspace(originalId);
      this.phase = phase;
      if (this.signedOutBy(rollbackError)) throw rollbackError;
      if (accessChanged) {
        this.reset();
        this.phase = 'failed';
        this.problem = this.describe(new ApiError(409, 'workspace_changed'));
        throw error;
      }
      if (!this.signedOutBy(error)) {
        this.problem = this.describe(error);
        if (phase === 'ready') this.connectLive();
      }
      throw error;
    } finally {
      this.workspaceSwitching = false;
      this.changed();
    }
  }

  async leaveWorkspace(): Promise<void> {
    if (this.workspaceSwitching) return;
    this.workspaceSwitching = true;
    this.changed();
    try {
      await this.canSwitchWorkspace();
      await this.api.leaveWorkspace();
      this.ui = { ...this.ui, draftDescription: '', draftProjectId: null, sessionSearch: '', entrySelection: [] };
      await this.reload();
    } finally { this.workspaceSwitching = false; this.changed(); }
  }

  private reset(): void {
    this.generation += 1;
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    this.stopLive?.();
    this.stopLive = null;
    this.me = null;
    this.workspaces = null;
    this.api.setWorkspace(null);
    this.ws = emptyWorkspace();
    this.ranges.clear();
    this.sessions.clear();
    this.sheet = null;
    this.problem = null;
    this.notice = null;
    this.ui = { ...this.ui, entrySelection: [], sessionSearch: '', draftDescription: '', draftProjectId: null };
    this.seq = 0;
    this.pending = 0;
    this.lastSaved = null;
  }

  async refreshMe(): Promise<void> {
    if (this.workspaceSwitching) return;
    const generation = this.generation;
    const request = ++this.meRequest;
    try {
      const me = await this.api.me();
      if (generation !== this.generation || request !== this.meRequest) return;
      if (me?.user.id !== this.me?.user.id || me?.workspace?.memberId !== this.me?.workspace?.memberId
        || me?.workspace?.id !== this.me?.workspace?.id || me?.workspace?.role !== this.me?.workspace?.role) {
        await this.reload();
        return;
      }
      this.me = me;
      this.changed();
    } catch (error) {
      if (generation === this.generation && request === this.meRequest) throw error;
    }
  }

  // MARK: Loading

  private async loadBase(): Promise<void> {
    const generation = this.generation;
    const base = await this.api.base();
    if (generation !== this.generation) return;
    const workspace = this.me?.workspace;
    if (base.you && (base.you.memberId !== workspace?.memberId || base.you.role !== (workspace?.role ?? 'member'))) {
      throw new ApiError(409, 'workspace_changed');
    }
    this.ws = workspaceFrom({ ...base, you: base.you ?? { memberId: workspace?.memberId ?? null, role: workspace?.role === 'owner' ? 'owner' : 'member' } });
    this.seq = Math.max(this.seq, base.seq);
  }

  /** Keeps a range loaded while a screen shows it. Returns the release function. */
  retainRange(start: number, end: number, usage: boolean): () => void {
    const key = rangeKey(start, end, usage);
    let entry = this.ranges.get(key);
    if (!entry) {
      entry = { key, start, end, usage, data: undefined, loading: false, error: null, retained: 0, stale: true, lastUsed: this.clock() };
      this.ranges.set(key, entry);
    }
    entry.retained += 1;
    entry.lastUsed = this.clock();
    if (entry.stale && !entry.loading) void this.fetchRange(entry);
    return () => {
      const e = this.ranges.get(key);
      if (e !== entry) return;
      e.retained -= 1;
      e.lastUsed = this.clock();
      this.evict();
    };
  }

  /** A loaded range: your own time, or with `team` the whole team's (owners in a team; the same data otherwise). */
  range(start: number, end: number, usage: boolean, team = false): Loadable<RangeData> {
    const entry = this.ranges.get(rangeKey(start, end, usage));
    if (!entry) return { data: undefined, loading: true, error: null };
    return (team && this.ws.team.role === 'owner') || !entry.mine ? entry : { data: entry.mine, loading: entry.loading, error: entry.error };
  }

  /** Unretained ranges are kept a while (back and forth between screens), up to 24. */
  private evict(): void {
    const idle = [...this.ranges.values()].filter((e) => e.retained <= 0).sort((a, b) => b.lastUsed - a.lastUsed);
    for (const e of idle.slice(24)) this.ranges.delete(e.key);
  }

  private async fetchRange(entry: RangeEntry): Promise<void> {
    const generation = this.generation;
    const current = () => generation === this.generation && this.ranges.get(entry.key) === entry;
    entry.loading = true;
    entry.stale = false;
    this.changed();
    try {
      const response = await this.api.range(entry.start, entry.end, entry.usage);
      if (!current()) return;
      entry.data = rangeFrom(response, { start: entry.start, end: entry.end }, this.ws.team);
      // In a team the screens show your own time; the server sends owners everyone's.
      const you = this.ws.team.you;
      entry.mine = you ? onlyMember(entry.data, you) : undefined;
      entry.error = null;
    } catch (error) {
      if (!current()) return;
      if (error instanceof ApiError && error.code === 'workspace_changed') { await this.reload(); return; }
      if (this.signedOutBy(error)) return;
      entry.error = this.describe(error);
    } finally {
      if (!current()) return;
      entry.loading = false;
      this.changed();
      if (entry.stale && entry.retained > 0) void this.fetchRange(entry);
    }
  }

  retainSession(id: string): () => void {
    let entry = this.sessions.get(id);
    if (!entry) {
      entry = { id, data: undefined, loading: false, error: null, retained: 0, stale: true, events: undefined };
      this.sessions.set(id, entry);
    }
    entry.retained += 1;
    if (entry.stale && !entry.loading) void this.fetchSession(entry);
    return () => {
      const e = this.sessions.get(id);
      if (e === entry) e.retained -= 1;
    };
  }

  session(id: string): Loadable<RangeData | null> & { events: RawEvent[] | undefined } {
    return this.sessions.get(id) ?? { data: undefined, loading: true, error: null, events: undefined };
  }

  private async fetchSession(entry: SessionEntry): Promise<void> {
    const generation = this.generation;
    const current = () => generation === this.generation && this.sessions.get(entry.id) === entry;
    entry.loading = true;
    entry.stale = false;
    this.changed();
    try {
      const [response, events] = await Promise.all([this.api.session(entry.id), this.api.events(entry.id).catch(() => [] as RawEvent[])]);
      if (!current()) return;
      entry.data = response ? sessionRangeFrom(response, this.ws.team) : null;
      entry.events = events;
      entry.error = null;
    } catch (error) {
      if (!current()) return;
      if (error instanceof ApiError && error.code === 'workspace_changed') { await this.reload(); return; }
      if (this.signedOutBy(error)) return;
      entry.error = this.describe(error);
    } finally {
      if (!current()) return;
      entry.loading = false;
      this.changed();
      if (entry.stale && entry.retained > 0) void this.fetchSession(entry);
    }
  }

  versions(entryId: string): Promise<TimeEntryVersion[]> {
    return this.api.versions(entryId);
  }

  /** An entry from whatever is loaded (the editor opens from screens that show it). */
  findEntry(id: string): TimeEntry | undefined {
    for (const entry of this.ranges.values()) {
      const found = entry.data?.entries.find((e) => e.id === id);
      if (found) return found;
    }
    return undefined;
  }

  /** Another entry of yours that is running (only one timer may run). */
  otherRunning(id: string): boolean {
    for (const entry of this.ranges.values()) {
      if (entry.data?.entries.some((e) => e.id !== id && e.endAt === null && e.deletedAt === null && e.memberId === this.me?.workspace?.memberId)) return true;
    }
    return false;
  }

  // MARK: Live

  private connectLive(): void {
    const generation = this.generation;
    this.stopLive?.();
    this.live = 'connecting';
    this.stopLive = this.api.live({
      onChange: (seq) => {
        if (generation !== this.generation) return;
        if (seq === 0 || seq > this.seq) this.scheduleRefresh();
      },
      onConnected: (connected) => {
        if (generation !== this.generation) return;
        const was = this.live;
        this.live = connected ? 'connected' : 'offline';
        // Back after a gap: anything may have changed meanwhile.
        if ((connected && was === 'offline') || (!connected && was === 'connected')) this.scheduleRefresh();
        this.changed();
      },
    });
  }

  /** Reloads the workspace and everything on screen (debounced: one reload for a burst of changes). */
  scheduleRefresh(delay = 250): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      void this.refresh();
    }, delay);
  }

  async refresh(): Promise<void> {
    if (this.workspaceSwitching) return;
    const generation = this.generation;
    try {
      await this.refreshMe();
      if (generation !== this.generation) return;
      await this.loadBase();
    } catch (error) {
      if (generation !== this.generation) return;
      if (error instanceof ApiError && error.code === 'workspace_changed') { await this.reload(); return; }
      if (this.signedOutBy(error)) return;
      this.problem = this.describe(error);
    }
    if (generation !== this.generation) return;
    for (const entry of this.ranges.values()) {
      entry.stale = true;
      if (entry.retained > 0 && !entry.loading) void this.fetchRange(entry);
    }
    for (const entry of this.sessions.values()) {
      entry.stale = true;
      if (entry.retained > 0 && !entry.loading) void this.fetchSession(entry);
    }
    this.changed();
  }

  // MARK: Changes

  /** Applies changes here at once, then sends them. On failure everything reloads and a banner explains. */
  async apply(changes: Change[]): Promise<boolean> {
    const generation = this.generation;
    if (changes.length === 0) return true;
    if (this.workspaceSwitching) return false;
    // Members track their own time; projects, clients, rules and settings are the owners' (the server checks too).
    if (this.ws.team.role === 'member' && changes.some((c) => ['projects', 'clients', 'projectRules', 'settings'].includes(c.entity))) {
      this.problem = this.t('Nur Inhaber ändern Projekte, Kunden, Regeln und Einstellungen.');
      this.changed();
      return false;
    }
    for (const change of changes) this.applyLocal(change);
    this.pending += 1;
    this.changed();
    try {
      const results = await this.api.push(changes);
      if (generation !== this.generation) return false;
      const refused = results.filter((r) => r !== 'ok' && r !== 'stale').length;
      if (refused > 0) throw new ApiError(422, 'invalid');
      if (results.includes('stale')) this.scheduleRefresh(0);
      this.lastSaved = this.clock();
      return true;
    } catch (error) {
      if (generation !== this.generation) return false;
      if (error instanceof ApiError && error.code === 'workspace_changed') { await this.reload(); return false; }
      if (this.signedOutBy(error)) return false;
      this.problem = this.describe(error);
      this.scheduleRefresh(0);
      return false;
    } finally {
      if (generation === this.generation) {
        this.pending -= 1;
        this.changed();
      }
    }
  }

  dismissProblem(): void {
    this.problem = null;
    this.changed();
  }

  private applyLocal(change: Change): void {
    const ws = this.ws;
    const upsert = <T extends { id: string }>(list: T[], item: T) => [...list.filter((x) => x.id !== item.id), item];
    const withRanges = (fn: (data: RangeData) => RangeData) => {
      for (const entry of this.ranges.values()) if (entry.data) {
        entry.data = fn(entry.data);
        const you = this.ws.team.you;
        entry.mine = you ? onlyMember(entry.data, you) : undefined;
      }
      for (const entry of this.sessions.values()) if (entry.data) entry.data = fn(entry.data);
    };
    if (change.op === 'delete') {
      switch (change.entity) {
        case 'projectRules': this.ws = { ...ws, rules: ws.rules.filter((r) => r.id !== change.id) }; break;
        case 'settings': {
          const settings = new Map(ws.settings);
          settings.delete(change.id as never);
          this.ws = { ...ws, settings };
          break;
        }
        case 'agentSessions':
          withRanges((d) => {
            if (!d.sessions.some((s) => s.id === change.id)) return d;
            const spans = new Map(d.spans);
            spans.delete(change.id);
            const edits = new Map(d.edits);
            edits.delete(change.id);
            return { ...d, sessions: d.sessions.filter((s) => s.id !== change.id), spans, edits };
          });
          for (const entry of this.sessions.values()) if (entry.id === change.id) entry.data = null;
          break;
        default: break;
      }
      return;
    }
    switch (change.entity) {
      case 'projects': this.ws = { ...ws, projects: upsert(ws.projects, change.data) }; break;
      case 'clients': this.ws = { ...ws, clients: upsert(ws.clients, change.data) }; break;
      case 'projectRules': this.ws = { ...ws, rules: upsert(ws.rules, change.data) }; break;
      case 'settings': {
        const settings = new Map(ws.settings);
        settings.set(change.data.key, change.data.value);
        this.ws = { ...ws, settings };
        break;
      }
      case 'timeEntries': {
        const e = change.data;
        withRanges((d) => {
          const inside = e.deletedAt === null && e.startAt < d.range.end && (e.endAt === null || e.endAt > d.range.start);
          const entries = d.entries.filter((x) => x.id !== e.id);
          if (inside) entries.push(e);
          entries.sort((a, b) => a.startAt - b.startAt);
          return { ...d, entries };
        });
        break;
      }
      case 'agentSessions': {
        const s = change.data;
        withRanges((d) => (d.sessions.some((x) => x.id === s.id) ? { ...d, sessions: d.sessions.map((x) => (x.id === s.id ? s : x)) } : d));
        break;
      }
      case 'spanEdits': {
        const e = change.data;
        withRanges((d) => {
          if (!d.sessions.some((s) => s.id === e.sessionId)) return d;
          const edits = new Map(d.edits);
          edits.set(e.sessionId, [...(edits.get(e.sessionId) ?? []).filter((x) => x.id !== e.id), e].sort((a, b) => a.createdAt - b.createdAt));
          return { ...d, edits };
        });
        break;
      }
      default: break;
    }
  }

  /** A 401 anywhere means the session ended (expired, revoked elsewhere, account deleted): back to sign-in. */
  private signedOutBy(error: unknown): boolean {
    if (!(error instanceof ApiError) || error.status !== 401 || this.phase !== 'ready') return false;
    this.reset();
    this.phase = 'signedOut';
    this.problem = this.describe(error);
    this.changed();
    return true;
  }

  describe(error: unknown): string {
    const t = this.t;
    if (error instanceof ApiError) {
      if (error.code === 'offline') return t('Keine Verbindung zum Server. Änderungen werden nicht gesichert, bis sie wieder steht.');
      if (error.code === 'guest_storage') return t('Dieser Browser speichert gerade keine Daten. Die Änderung ist nur bis zum Neuladen da; erstelle ein Konto, um sie zu sichern.');
      if (error.code === 'guest_changed') return t('Die Gastdaten wurden in einem anderen Tab übernommen oder gelöscht. Lade die Seite neu, um fortzufahren.');
      if (error.code === 'account_required') return t('Dafür brauchst du ein Konto.');
      if (error.code === 'code_invalid') return t('Diesen Code gibt es nicht, oder er gilt nicht mehr.');
      if (error.code === 'code_used') return t('Dieser Code wurde schon eingelöst.');
      if (error.code === 'pro_required') return t('Das gibt es mit Pro.');
      if (error.status === 401) return t('Deine Anmeldung ist abgelaufen. Bitte melde dich neu an.');
      if (error.status === 429) return t('Zu viele Versuche. Warte ein paar Minuten.');
      if (error.code === 'invalid') return t('Der Server hat eine Änderung abgelehnt. Die Ansicht wurde neu geladen.');
      switch (error.code) {
        case 'mail_not_configured': return t('Der E-Mail-Versand ist auf diesem Server noch nicht eingerichtet.');
        case 'auth_provider_unavailable':
        case 'auth_unavailable': return t('Die Anmeldung ist gerade nicht erreichbar. Versuche es später erneut.');
        case 'provider_not_configured': return t('Die Anmeldung ist auf diesem Server noch nicht eingerichtet.');
        case 'auth_provider_changed': return t('Der Anmeldedienst dieses Servers hat sich geändert. Lade Tellary neu und melde dich erneut an.');
        case 'verified_email_required': return t('Bitte bestätige deine E-Mail-Adresse, bevor du dich bei Tellary anmeldest.');
        case 'email_already_in_use': return t('Diese E-Mail-Adresse gehört bereits zu einem anderen Konto.');
        case 'identity_already_linked': return t('Diese Anmeldung ist bereits mit einem anderen Tellary-Konto verknüpft.');
        case 'account_deletion_pending': return t('Die Kontolöschung ist noch nicht abgeschlossen. Versuche die Kontolöschung erneut.');
        case 'owner_only': return t('Das dürfen nur Inhaber des Arbeitsbereichs.');
        case 'last_owner': return t('Der Arbeitsbereich braucht mindestens einen Inhaber.');
        case 'already_member': return t('Die Person ist schon im Team.');
        case 'cannot_remove_yourself': return t('Dich selbst kannst du nicht entfernen.');
        case 'workspace_changed': return t('Der Arbeitsbereich wurde in einem anderen Tab gewechselt. Lade die Ansicht neu.');
        case 'workspace_access_revoked':
        case 'workspace_forbidden': return t('Du hast keinen Zugang zu diesem Arbeitsbereich.');
        case 'invite_unavailable': return t('Diese Einladung ist abgelaufen, zurückgezogen oder für eine andere E-Mail-Adresse.');
        case 'personal_workspace':
        case 'private_workspace': return t('Dein privater Arbeitsbereich kann nicht geteilt oder verlassen werden.');
        case 'changes_pending': return t('Warte, bis deine Änderungen gespeichert sind, bevor du den Arbeitsbereich wechselst.');
        case 'timer_running': return t('Stoppe zuerst deinen laufenden Timer, bevor du den Arbeitsbereich wechselst.');
        case 'stripe_key_invalid': return t('Das ist kein Stripe-Schlüssel. Er beginnt mit sk_ oder rk_.');
        case 'stripe_auth': return t('Stripe kennt diesen Schlüssel nicht.');
        case 'stripe_permission': return t('Dem Schlüssel fehlen Rechte: %@', error.detail ?? '');
        case 'stripe_invalid': return t('Stripe hat abgelehnt: %@', error.detail ?? '');
        case 'stripe_unreachable': return t('Stripe ist gerade nicht erreichbar. Versuch es gleich noch einmal.');
        case 'stripe_not_connected': return t('Verbinde zuerst Stripe unter Einstellungen › Rechnungen.');
        case 'stripe_unavailable': return t('Auf diesem Server ist Stripe nicht eingerichtet (SECRETS_KEY fehlt).');
        case 'client_required': return t('Für Stripe braucht die Rechnung einen Kunden.');
      }
      return t('Der Server antwortet nicht wie erwartet (%@).', error.code);
    }
    return t('Etwas ist schiefgelaufen');
  }
}

export { emptyRange };
